import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  username: string;
  sp_wins: number;
  sp_losses: number;
  mp_wins: number;
  mp_losses: number;
  mp_draws: number;
  total_matches: number;
  best_streak: number;
  current_streak: number;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  profileMissing: boolean;
  isGuest: boolean;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  profile: null,
  profileMissing: false,
  isGuest: true,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export { AuthContext };
export type { AuthState, Profile };

export function useAuthProvider(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const userIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const profileLoadedRef = useRef(false);
  const loadingClearedRef = useRef(false);

  const clearAuthState = useCallback(() => {
    userIdRef.current = null;
    setUser(null);
    setProfile(null);
    setProfileMissing(false);
    setIsAdmin(false);
  }, []);

  const forceSignOut = useCallback(async () => {
    console.warn('Invalid player session — dropping to guest');
    clearAuthState();
    setLoading(false);
    loadingClearedRef.current = true;
    await supabase.auth.signOut();
  }, [clearAuthState]);

  const fetchProfile = useCallback(async (userId: string, authUser?: User | null) => {
    console.log('[useAuth] Fetching profile for user:', userId);
    
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          console.error('[useAuth] Profile fetch timed out after 4s');
          resolve(null);
        }, 4000);
      });
      
      // Race between fetch and timeout
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (result === null) {
        // Timeout occurred
        console.warn('[useAuth] Continuing without profile due to timeout');
        return false;
      }
      
      const { data, error } = result;

      if (error) {
        console.error('[useAuth] Profile fetch error:', error);
        // Only force sign out on auth errors
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          await forceSignOut();
          return false;
        }
        console.warn('[useAuth] Profile fetch failed but continuing');
        return false;
      }

      let resolvedProfile = data as Profile | null;

      if (!resolvedProfile) {
        console.log('[useAuth] Profile missing, attempting self-heal');
        const fallbackUser = authUser ?? (await supabase.auth.getUser()).data.user;
        const nickname = fallbackUser?.user_metadata?.username || fallbackUser?.email?.split('@')[0] || 'Duelist';
        const { error: upsertErr } = await supabase
          .from('profiles')
          .upsert({ id: userId, username: nickname }, { onConflict: 'id' });

        if (upsertErr) {
          console.error('Profile self-heal failed:', upsertErr);
          // Don't sign out, just continue without profile
          return false;
        }

        const { data: newData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (!newData) {
          console.warn('[useAuth] Profile refetch failed after upsert');
          return false;
        }

        resolvedProfile = newData as Profile;
      }

      setProfile(resolvedProfile);
      profileLoadedRef.current = true;
      setProfileMissing(false);

      // Fetch admin role - don't fail if this errors
      try {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'admin')
          .maybeSingle();

        setIsAdmin(!!roleData);
      } catch (roleErr) {
        console.warn('[useAuth] Role fetch failed:', roleErr);
        setIsAdmin(false);
      }

      console.log('[useAuth] Profile fetch completed successfully');
      return true;
    } catch (err) {
      console.error('[useAuth] Unexpected error in fetchProfile:', err);
      // Don't force sign out on unexpected errors - just continue
      return false;
    }
  }, [forceSignOut]);

  const refreshProfile = useCallback(async () => {
    if (!userIdRef.current) return;

    setLoading(true);
    await fetchProfile(userIdRef.current);
    // Always set loading to false after fetch completes
    setLoading(false);
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    clearAuthState();
    profileLoadedRef.current = false;
    setLoading(false);
    await supabase.auth.signOut();
  }, [clearAuthState]);

  useEffect(() => {
    let active = true;
    loadingClearedRef.current = false;

    const setSessionUser = async (session: Session | null) => {
      if (!active) return;

      if (!session?.user) {
        clearAuthState();
        setLoading(false);
        loadingClearedRef.current = true;
        return;
      }

      const uid = session.user.id;
      console.log('[useAuth] Setting session user:', uid);
      userIdRef.current = uid;
      setUser(session.user);
      setLoading(true);

      const ok = await fetchProfile(uid, session.user);
      // Always set loading to false, regardless of profile fetch result
      if (active) {
        console.log('[useAuth] Profile fetch completed, ok:', ok);
        setLoading(false);
        loadingClearedRef.current = true;
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[useAuth] Auth event:', event);
        if (event === 'INITIAL_SESSION') return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (!session?.user) {
            console.warn('[useAuth] SIGNED_IN event but no session user');
            clearAuthState();
            setLoading(false);
            loadingClearedRef.current = true;
            return;
          }

          const uid = session.user.id;
          if (userIdRef.current === uid && initializedRef.current) {
            console.log('[useAuth] Ignoring duplicate SIGNED_IN for same user');
            return;
          }

          console.log('[useAuth] Processing SIGNED_IN event for user:', uid);
          userIdRef.current = uid;
          setUser(session.user);
          setLoading(true);

          const ok = await fetchProfile(uid, session.user);
          // Always set loading to false, regardless of profile fetch result
          if (active) {
            console.log('[useAuth] Profile fetch completed, ok:', ok);
            setLoading(false);
            loadingClearedRef.current = true;
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('[useAuth] SIGNED_OUT event');
          clearAuthState();
          profileLoadedRef.current = false;
          setLoading(false);
          loadingClearedRef.current = true;
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!active) return;
      await setSessionUser(session);
      initializedRef.current = true;
    }).catch((err) => {
      console.error('getSession failed:', err);
      if (!active) return;
      clearAuthState();
      setLoading(false);
      loadingClearedRef.current = true;
      initializedRef.current = true;
    });

    // Failsafe: ALWAYS clear loading after 10 seconds no matter what
    const maxTimeout = setTimeout(() => {
      if (!active) return;
      if (!loadingClearedRef.current) {
        console.error('[useAuth] FAILSAFE: Force clearing loading state after 10s timeout');
        setLoading(false);
        loadingClearedRef.current = true;
      }
    }, 10000);

    // Monitor initialization - be more lenient with timeouts
    const timeout = setTimeout(() => {
      if (!active) return;
      
      if (!initializedRef.current) {
        // getSession never resolved — network issue
        console.warn('[useAuth] Auth initialization timeout after 12s - assuming guest');
        clearAuthState();
        setLoading(false);
        loadingClearedRef.current = true;
        initializedRef.current = true;
      } else if (userIdRef.current && !profileLoadedRef.current) {
        // Session exists but profile slow — just clear loading, don't sign out
        console.warn('[useAuth] Profile load slow - clearing loading state anyway');
        // Try one more fetch but don't force sign out on failure
        void fetchProfile(userIdRef.current).then(ok => {
          if (!ok) {
            console.warn('[useAuth] Profile retry failed - continuing as guest');
          }
        });
        if (!loadingClearedRef.current) {
          setLoading(false);
          loadingClearedRef.current = true;
        }
      } else if (!userIdRef.current && !loadingClearedRef.current) {
        // No session, ensure loading is false
        setLoading(false);
        loadingClearedRef.current = true;
      }
    }, 12000);  // Increased from 8s to 12s

    return () => {
      active = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
      clearTimeout(maxTimeout);
    };
  }, [clearAuthState, fetchProfile, forceSignOut]);

  return {
    user,
    profile,
    profileMissing,
    isGuest: !user,
    isAdmin,
    loading,
    signOut,
    refreshProfile,
  };
}
